import React, { useState } from "react";
import toast from "react-hot-toast";
import api from "../../../api/Api.js";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Swal from "sweetalert2";

const AddPackagesName = () => {
  // =========================
  // NAVIGATE
  // =========================

  const navigate = useNavigate();

  // =========================
  // STATE
  // =========================

  const [countryRows, setCountryRows] = useState([
    {
      packageName: "",
      slug: "",
    },
  ]);

  // =========================
  // ADD ROW
  // =========================

  const addCountryRow = () => {
    setCountryRows((prevRows) => [
      ...prevRows,
      {
        packageName: "",
        slug: "",
      },
    ]);
  };

  // =========================
  // REMOVE ROW
  // =========================

  const removeCountryRow = async (index) => {
    if (countryRows.length === 1) {
      toast.error("At least one row is required");
      return;
    }

    const confirm = await Swal.fire({
      title: "Delete Row?",
      text: "Are you sure you want to delete this row?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
    });

    if (!confirm.isConfirmed) return;

    setCountryRows((prevRows) =>
      prevRows.filter((_, i) => i !== index)
    );
  };

  // =========================
  // HANDLE INPUT CHANGE
  // =========================

  const handleCountryChange = (index, field, value) => {
    setCountryRows((prevRows) => {
      const updatedRows = [...prevRows];

      updatedRows[index] = {
        ...updatedRows[index],
        [field]: value,
      };

      // AUTO GENERATE SLUG
      if (field === "packageName") {
        updatedRows[index].slug = value
          .toLowerCase()
          .trim()
          .replace(/[^\w\s-]/g, "")
          .replace(/\s+/g, "-")
          .replace(/-+/g, "-");
      }

      return updatedRows;
    });
  };

  // =========================
  // SAVE PACKAGE NAME
  // =========================

  const saveCountry = async () => {
    // Check empty package name
    const hasEmpty = countryRows.some(
      (row) => !row.packageName.trim()
    );

    if (hasEmpty) {
      toast.error("Package Name is required");
      return;
    }

    // Check empty slug
    const hasEmptySlug = countryRows.some(
      (row) => !row.slug.trim()
    );

    if (hasEmptySlug) {
      toast.error("Slug is required");
      return;
    }

    // Confirmation
    const confirm = await Swal.fire({
      title: "Save Package Name?",
      text: "Do you want to save these Package Names?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Save",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#0d6efd",
    });

    if (!confirm.isConfirmed) return;

    try {
      // API payload
      const payload = {
        packageNamedata: countryRows,
      };

      console.log("PAYLOAD =>", payload);

      const res = await api.post(
        "/adminHolidays/save-package-name",
        payload
      );

      // SUCCESS
      if (res?.data?.success) {
        await Swal.fire({
          title: "Success",
          text: "Package Name Saved Successfully",
          icon: "success",
          confirmButtonText: "OK",
        });

        // RESET
        setCountryRows([
          {
            packageName: "",
            slug: "",
          },
        ]);

        // NAVIGATE
        navigate("/holidays/packages/manage-packages");
      } else {
        Swal.fire({
          title: "Error",
          text: res?.data?.message || "Something went wrong",
          icon: "error",
        });
      }
    } catch (error) {
      console.error("FULL ERROR =>", error);
      console.error("RESPONSE DATA =>", error?.response?.data);

      toast.error(
        error?.response?.data?.message || "Server Error"
      );
    }
  };

  // =========================
  // JSX
  // =========================

  return (
    <div className="container-fluid py-3 bg-white min-vh-100">
      {/* HEADER */}
      <div className="card shadow-sm border-0 mb-4 bg-primary-subtle rounded-3">
        <div className="card-body d-flex justify-content-between align-items-center">
          <h4 className="fw-bold mb-0">
            Add Packages Name
          </h4>

          <Link
            to="/packages/manage-packages"
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2"
          >
            <ArrowLeft size={16} />
            Back
          </Link>
        </div>
      </div>

      <div className="row">
        {/* TABLE */}
        <div className="col-lg-12">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <table className="table table-hover table-bordered align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th width="50">Sl.No</th>

                    <th>Packages Name</th>

                    <th>Slug</th>

                    <th width="120">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {countryRows.map((row, index) => (
                    <tr key={index}>
                      {/* SL.NO */}
                      <td>{index + 1}</td>

                      {/* PACKAGE NAME */}
                      <td>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter Package Name"
                          value={row.packageName}
                          onChange={(e) =>
                            handleCountryChange(
                              index,
                              "packageName",
                              e.target.value
                            )
                          }
                        />
                      </td>

                      {/* SLUG */}
                      <td>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Slug"
                          value={row.slug}
                          onChange={(e) =>
                            handleCountryChange(
                              index,
                              "slug",
                              e.target.value
                            )
                          }
                        />
                      </td>

                      {/* ACTION */}
                      <td>
                        <button
                          type="button"
                          className="btn btn-success btn-sm me-2"
                          onClick={addCountryRow}
                          title="Add Row"
                        >
                          <i className="fa fa-plus-square"></i>
                        </button>

                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() =>
                            removeCountryRow(index)
                          }
                          title="Delete Row"
                        >
                          <i className="fa fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* SAVE BUTTON */}
        <div className="col-lg-12 text-center">
          <button
            type="button"
            className="btn btn-info text-white px-5 mt-3"
            onClick={saveCountry}
          >
            Save Package Name
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddPackagesName;
